import {
  Component,
  computed,
  effect,
  inject,
  NgZone,
  OnInit,
  signal,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import AOS from 'aos';
import { InactivityService } from './shared/services/inactivity/inactivity-service.service';
import { SessionDialogComponent } from './shared/components/session-dialog/session-dialog.component';
import { AuthenticationService } from './shared/services/auth/authentication.service';
import { finalize, of, switchMap, throwError } from 'rxjs';
import { ApiService } from './shared/services/api/api.service';
import { AlertifyService } from './shared/services/alertify/alertify.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, SessionDialogComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'min-of-land-frontend';
  authenticationService: AuthenticationService = inject(AuthenticationService);

  isRefreshingToken = signal(false);
  isLoggingOut = signal(false);
  user = toSignal(this.authenticationService.user$);

  isProcessingInactivity = computed(() => {
    return this.isRefreshingToken() || this.isLoggingOut();
  });

  showTimeoutModal = computed(() => {
    const isLoggedIn = this.user();
    const hasTimedOut = this.inactivityService.timedOut();
    return isLoggedIn && hasTimedOut;
  });

  constructor(
    private readonly ngZone: NgZone,
    private readonly apiService: ApiService,
    private readonly alertifyService: AlertifyService,
    private readonly inactivityService: InactivityService,
  ) {
    effect(
      () => {
        const isLoggedIn = this.user();
        if (isLoggedIn) this.inactivityService.startWatching();
        else this.inactivityService.stopWatching();
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.initAOS();
  }

  initAOS() {
    this.ngZone.runOutsideAngular(() => {
      // You can also pass an optional settings object
      // below listed default settings
      AOS.init({
        // Global settings:
        disable: false, // accepts following values: 'phone', 'tablet', 'mobile', boolean, expression or function
        startEvent: 'DOMContentLoaded', // name of the event dispatched on the document, that AOS should initialize on
        initClassName: 'aos-init', // class applied after initialization
        animatedClassName: 'aos-animate', // class applied on animation
        useClassNames: false, // if true, will add content of `data-aos` as classes on scroll
        disableMutationObserver: false, // disables automatic mutations' detections (advanced)
        debounceDelay: 50, // the delay on debounce used while resizing window (advanced)
        throttleDelay: 99, // the delay on throttle used while scrolling the page (advanced)

        // Settings that can be overridden on per-element basis, by `data-aos-*` attributes:
        offset: 120, // offset (in px) from the original trigger point
        delay: 0, // values from 0 to 3000, with step 50ms
        duration: 600, // values from 0 to 3000, with step 50ms
        easing: 'ease', // default easing for AOS animations
        once: false, // whether animation should happen only once - while scrolling down
        mirror: false, // whether elements should animate out while scrolling past them
        anchorPlacement: 'top-bottom', // defines which position of the element regarding to window should trigger the animation
      });
    });
  }

  logout() {
    this.isLoggingOut.set(true);
    this.authenticationService
      .logout$()
      .pipe(finalize(() => this.isLoggingOut.set(false)))
      .subscribe();
  }

  refreshToken() {
    this.isRefreshingToken.set(true);

    const payload = {};
    this.apiService
      .refreshToken(payload)
      .pipe(
        switchMap((res) => {
          if (!res.success) {
            return throwError(
              () => res.message || 'Failed to get refresh token',
            );
          }
          return of(res);
        }),
        finalize(() => {
          this.isRefreshingToken.set(false);
          this.inactivityService.restartWatch();
        }),
      )
      .subscribe({
        next: () => {},
        error: (error) => {
          let errorMessage = this.apiService.getErrorMessage(error);
          this.alertifyService.error(errorMessage);
        },
      });
  }
}
